'use client';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useEffect, useRef, useState } from 'react';
import PaginatedGridInner, { type GridLifestyleCard } from './_Grid';
export type { GridLifestyleCard } from './_Grid';

interface PaginatedGridData {
  hits?: Array<any>;
  nbPages?: number;
  page?: number;
}

export interface PaginatedGridProps {
  gridLifestyleCard?: GridLifestyleCard;
  items?: Array<any>;
  nbPages?: number;
  page?: number;
  _data?: PaginatedGridData;
  loadData?: (
    params: { page: number },
    controller: AbortController,
    offset: number,
  ) => Promise<any>;
  content?: any;
  GridItemComponent: React.ComponentType<any>;
  isLoading?: boolean;
  loadingSlot?: React.ReactNode;
  className?: string;
  itemClassName?: string;
  isItemHidden?: (item: any) => boolean;
  darkBackground?: boolean;
  hideAddToCartButton?: boolean;
  hideDiscount?: boolean;
  showFinancing?: boolean;
  financingText?: boolean;
}

const PaginatedGrid = ({
  gridLifestyleCard,
  items: _items,
  nbPages,
  page: _page,
  _data,
  loadData,
  content,
  GridItemComponent,
  className,
  itemClassName,
  isItemHidden,
  hideAddToCartButton,
  hideDiscount,
  showFinancing,
  financingText,
  isLoading = false,
  loadingSlot,
  darkBackground = false,
}: PaginatedGridProps) => {
  const [items, setItems] = useState(_items ?? _data?.hits ?? []);
  const [totalPages, setTotalPages] = useState(nbPages ?? _data?.nbPages ?? 0);
  const tmpPage = _page ?? _data?.page;
  const [page, setPage] = useState(tmpPage ? tmpPage + 1 : 1);

  // Track load-more state for externally-controlled items so we can suppress
  // the button when a page returns no new visible items (e.g. all OOS after VIP filter).
  const awaitingLoadMore = useRef(false);
  const prevItemsLengthRef = useRef(_items?.length ?? 0);
  const prevDataKeyRef = useRef<string | undefined>();

  const viewMore = async () => {
    if (!loadData) return;
    try {
      const controller = new AbortController();
      if (_items !== undefined) {
        awaitingLoadMore.current = true;
      }
      const res = await loadData({ page }, controller, items.length);
      const { hits = [], nbPages = 0 } = res.data ? res.data : (res ?? {});
      if (hits.length > 0) {
        // When _items is externally controlled, the parent updates the prop and
        // the useEffect below syncs state. Skip the internal append here to avoid
        // the effect overwriting accumulated pagination state.
        if (_items === undefined) {
          setItems((curr) => [...curr, ...hits]);
        }
        setTotalPages(nbPages);
        setPage((prev) => prev + 1);
      }
    } catch (e) {
      awaitingLoadMore.current = false;
      if (!(e instanceof Error && e.name === 'AbortError')) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (_items === undefined) return;
    if (awaitingLoadMore.current) {
      // If the item count didn't grow, the loaded page was entirely filtered
      // client-side (e.g. all OOS for a VIP user). Suppress the load-more button.
      if (_items.length <= prevItemsLengthRef.current) {
        setTotalPages(0);
      }
      awaitingLoadMore.current = false;
    }
    prevItemsLengthRef.current = _items.length;
    setItems(_items);
  }, [_items]);

  useEffect(() => {
    if (!_data) return;
    // Build a lightweight key from the data to detect genuine changes
    // (e.g. filter resets) vs RSC re-renders resending the same page-0 data.
    const hitIds = _data.hits?.map((h) => h.id ?? h.deliveryKey ?? '').join(',') ?? '';
    const dataKey = `${_data.page}-${_data.nbPages}-${hitIds}`;
    if (dataKey === prevDataKeyRef.current) return;
    prevDataKeyRef.current = dataKey;
    setItems(_data.hits || []);
    setTotalPages(_data.nbPages || 0);
    setPage((_data.page ?? 0) + 1);
  }, [_data]);

  return (
    <div
      className={classNames(
        'flex flex-col sm:flex-row',
        darkBackground ? 'bg-neutral-600' : 'bg-warm-200',
        className,
      )}
    >
      <PaginatedGridInner
        items={items}
        totalPages={loadData ? totalPages : 0}
        isLoading={isLoading}
        loadingSlot={loadingSlot}
        page={page}
        viewMore={viewMore}
        gridLifestyleCard={gridLifestyleCard}
        content={content}
        GridItemComponent={GridItemComponent}
        hideAddToCartButton={hideAddToCartButton}
        hideDiscount={hideDiscount}
        showFinancing={showFinancing}
        financingText={financingText}
        itemClassName={itemClassName}
        isItemHidden={isItemHidden}
      />
    </div>
  );
};

export default PaginatedGrid;
